import { useMemo } from 'react';
import { ProductIdentity } from '@/components/products';
import { MarginBadge } from '@/components/products/MarginBadge';
import { MetricGrid, SummaryMetricCard, FormattedNumber } from '@/components/global';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  computePlanogramIncome,
  formatPlanogramGbp,
  PlanogramIncomeSlotInput,
} from '@/lib/planogramIncome';
import { formatPlanogramSlotLabel } from '@/lib/planogramSort';
import { Banknote, Percent, Receipt, TrendingUp } from 'lucide-react';
import { PlanogramSlotBadge } from './PlanogramSlotBadge';
import { PlanogramListingFeeInput } from './PlanogramListingFeeInput';

export interface PlanogramIncomePanelProps {
  slots: PlanogramIncomeSlotInput[];
  monthlyCogs: number;
  targetMargin: number;
  onMonthlyCogsChange: (raw: string) => void;
  onListingFeeChange: (slotIds: string[], raw: string) => void;
}

export function PlanogramIncomePanel({
  slots,
  monthlyCogs,
  targetMargin,
  onMonthlyCogsChange,
  onListingFeeChange,
}: PlanogramIncomePanelProps) {
  const { groups, summary } = useMemo(
    () => computePlanogramIncome(slots, { monthlyCogs }),
    [slots, monthlyCogs]
  );

  const hasFilledSlots = summary.filledSlots > 0;
  const totalCapacity = groups.reduce((sum, group) => sum + group.totalCapacity, 0);

  return (
    <div className="space-y-6">
      <MetricGrid className="grid-cols-5 lg:grid-cols-5">
        <SummaryMetricCard
          label={`Blended margin (target ${targetMargin.toFixed(0)}%)`}
          value={summary.blendedMargin != null ? `${summary.blendedMargin.toFixed(1)}%` : '—'}
          icon={Percent}
          status={
            summary.blendedMargin == null
              ? undefined
              : summary.blendedMargin >= targetMargin
                ? 'green'
                : 'red'
          }
        />
        <SummaryMetricCard
          label="Monthly COGS"
          value={formatPlanogramGbp(monthlyCogs)}
          icon={Receipt}
          labelHint="Machine overhead per month."
          editable={{
            id: 'planogram-monthly-cogs',
            inputValue: String(monthlyCogs),
            onInputChange: onMonthlyCogsChange,
            inputPlaceholder: '0.00',
            editAriaLabel: 'Edit monthly COGS',
          }}
        />
        <SummaryMetricCard
          label="Revenue (full sell-through)"
          value={hasFilledSlots ? formatPlanogramGbp(summary.revenueSellThrough) : '—'}
          icon={TrendingUp}
        />
        <SummaryMetricCard
          label="Profit (full sell-through)"
          value={hasFilledSlots ? formatPlanogramGbp(summary.profitSellThrough) : '—'}
          icon={Banknote}
        />
        <SummaryMetricCard
          label="Listing fees (monthly)"
          value={hasFilledSlots ? formatPlanogramGbp(summary.listingFeesMonthly) : '—'}
        />
      </MetricGrid>

      <Card className="overflow-hidden">
        {!hasFilledSlots ? (
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Assign products to slots on the Planogram tab to see income projections.
          </CardContent>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[16rem] pl-5">Product</TableHead>
                <TableHead>Slots</TableHead>
                <TableHead className="w-24 text-right">Capacity</TableHead>
                <TableHead className="w-28 text-right">Sale price</TableHead>
                <TableHead className="w-28 text-right">Cost price</TableHead>
                <TableHead className="w-28 text-right">Margin</TableHead>
                <TableHead className="w-28 text-right">Profit</TableHead>
                <TableHead className="w-28 pr-5 text-right">Listing fee</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((group) => (
                <TableRow key={group.key}>
                  <TableCell className="pl-5">
                    {group.product ? (
                      <ProductIdentity
                        productId={group.productId}
                        name={group.productName}
                        sku={group.product.sku}
                        size="sm"
                        showSku={false}
                        product={{
                          imageUrl: group.product.imageUrl,
                          media: group.product.media ?? undefined,
                        }}
                      />
                    ) : (
                      <p className="text-sm font-medium leading-snug">{group.productName}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {group.lines.map((line) => (
                        <PlanogramSlotBadge
                          key={line.slotId}
                          label={formatPlanogramSlotLabel(line.slotLabel, line.slotNumber)}
                        />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <FormattedNumber value={group.totalCapacity} />
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums">
                    {group.salePriceLabel}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums text-muted-foreground">
                    {group.costPriceLabel}
                  </TableCell>
                  <TableCell className="text-right">
                    {group.blendedMargin != null ? (
                      <MarginBadge
                        marginPercent={group.blendedMargin}
                        targetMargin={targetMargin}
                      />
                    ) : (
                      <span
                        className="text-sm text-muted-foreground"
                        title="Set a purchase cost on this product to calculate margin"
                      >
                        Cost n/a
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPlanogramGbp(group.profitSellThrough)}
                  </TableCell>
                  <TableCell className="pr-5 text-right text-sm tabular-nums">
                    <PlanogramListingFeeInput
                      slotIds={group.lines.map((line) => line.slotId)}
                      value={group.lines[0]?.listingFeeMonthly ?? 0}
                      varies={group.listingFeeLabel === 'Varies'}
                      onChange={onListingFeeChange}
                    />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/40 font-medium">
                <TableCell className="pl-5" colSpan={2}>
                  Machine total
                </TableCell>
                <TableCell className="text-right">
                  <FormattedNumber value={totalCapacity} />
                </TableCell>
                <TableCell colSpan={3} />
                <TableCell className="text-right tabular-nums">
                  {formatPlanogramGbp(summary.profitSellThrough)}
                </TableCell>
                <TableCell className="pr-5 text-right tabular-nums">
                  {formatPlanogramGbp(summary.listingFeesMonthly)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export default PlanogramIncomePanel;
